import { ifDefined } from 'lit/directives/if-defined.js'
import { classMap } from 'lit/directives/class-map.js'
import { html, literal } from 'lit/static-html.js'
import { property } from 'lit/decorators.js'
import { ref } from 'lit/directives/ref.js'
import { nothing } from 'lit'

import { WithFocus, AsSubmitter, anchor, compose } from '#utils/mixins'
import { ariaElements, delegateEvents } from '#utils/directives'
import styles from './lfds-shortcut.css.ts'
import { LfuiElement } from '#utils/element'

const LINK = literal`a`
const BUTTON = literal`button`

/**
 * @name LfdsShortcut
 *
 * @deprecated Moved to `@lansforsakringar/core-recipes/shortcut` as `<lfds-r-shortcut>`.
 * @status deprecated
 * @tag lfds-shortcut
 *
 * @slot default - Text label
 * @slot description - Text description
 */
export default class LfdsShortcut extends compose(
	LfuiElement,
	AsSubmitter,
	WithFocus
) {
	static styles = [LfuiElement.styles, styles]

	@property() label: string
	@property() description: string
	@property() layout: 'vertical' | 'horizontal' = 'horizontal'
	@property() type: 'button' | 'submit' | 'reset' | 'menu'
	@property() target: '_blank' | '_self'
	@property() download: string | boolean
	@property() href: string
	@property() rel: string
	@property() name: string
	@property() value: string | number
	@property({ attribute: 'icon-id' }) iconId: string
	@property({ attribute: 'validation-message' }) validationMessage: string
	@property({ attribute: 'aria-labelledby' }) ariaLabelledby: string
	@property({ attribute: 'aria-controls' }) ariaControls: string
	@property({ attribute: 'aria-expanded' }) ariaExpanded: string

	render() {
		const tag = this.href != null ? LINK : BUTTON

		return html`
      <${tag}
        class=${classMap({
					shortcut: true,
					['focus-visible']: this.focusVisible,
					vertical: this.layout === 'vertical'
				})}
        aria-expanded=${ifDefined(this.ariaExpanded)}
        rel=${ifDefined(this.rel)}
        href=${ifDefined(this.href)}
        name=${ifDefined(this.name)}
        type=${ifDefined(this.type)}
        value=${ifDefined(this.value)}
        target=${ifDefined(this.target)}
        download=${this.download ? this.download || '' : nothing}
        ${ref(this[anchor])}
        ${delegateEvents(this.delegatedEvents, {
					after: { click: this.handleClick }
				})}
        ${ariaElements({
					labelledBy: this.ariaLabelledby,
					controls: this.ariaControls
				})}>
        ${
					this.iconId
						? html`
								<lfds-icon
									class="icon"
									icon-id=${this.iconId}
									size=${this.layout === 'vertical' ? '40' : '32'}
								></lfds-icon>
							`
						: nothing
				}
        <span class="content">
          <slot>
            <span class="label">${this.label}</span>
          </slot>
          <slot name="description">
            ${
							this.description?.trim()
								? html` <span class="description">${this.description}</span>`
								: nothing
						}
          </slot>
        </span>
        ${
					this.layout === 'vertical'
						? nothing
						: html`
								<lfds-icon
									class="arrow"
									icon-id="arrow-right"
									size="32"
								></lfds-icon>
							`
				}
      </${tag}>
    `
	}
}
